import {
  ApolloError,
  DocumentNode,
  LazyQueryHookOptions,
  MutationHookOptions,
  OperationVariables,
  QueryHookOptions,
  useLazyQuery as useApolloLazyQuery,
  useMutation as useApolloMutation,
  useQuery as useApolloQuery,
} from '@apollo/client';

// import { useAuthStore } from '@/shared/store/auth.store';
import { useSubscriptionStore } from '@/_app/providers/SubscriptionLockScreenProvider/useSubscriptionStore';
import { isSubscriptionError } from '@/shared/api';

type SubscriptionError = {
  errors: { extensions: { code: string } }[];
};

const useCheckGqlSubscriptionError = () => {
  const setIsSubscriptionActive = useSubscriptionStore((s) => s.setIsSubscriptionActive);

  const onError = (error: ApolloError) => {
    if (!error.networkError) {
      return;
    }

    if (!('result' in error.networkError)) {
      return;
    }

    const networkErrorRes = error.networkError.result as string | SubscriptionError;

    if (typeof networkErrorRes === 'string') {
      return;
    }

    const errorCode = networkErrorRes?.errors?.[0].extensions.code;

    if (isSubscriptionError(errorCode)) {
      setIsSubscriptionActive({ isActive: false, isRegistered: true });
    }
  };

  return { onError };
};

export const useQuery = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  documentNode: DocumentNode,
  baseOptions: QueryHookOptions<TData, TVariables>,
) => {
  const { onError } = useCheckGqlSubscriptionError();

  return useApolloQuery<TData, TVariables>(documentNode, {
    ...baseOptions,
    onError: (error) => {
      onError(error);
      baseOptions.onError?.(error);
    },
  });
};
export const useMutation = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  documentNode: DocumentNode,
  baseOptions: MutationHookOptions<TData, TVariables>,
) => {
  const { onError } = useCheckGqlSubscriptionError();

  return useApolloMutation<TData, TVariables>(documentNode, {
    ...baseOptions,
    onError: (error, clientOptions) => {
      onError(error);
      baseOptions?.onError?.(error, clientOptions);
    },
  });
};
export const useLazyQuery = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  documentNode: DocumentNode,
  baseOptions: LazyQueryHookOptions<TData, TVariables>,
) => {
  const { onError } = useCheckGqlSubscriptionError();

  return useApolloLazyQuery<TData, TVariables>(documentNode, {
    ...baseOptions,
    onError: (error) => {
      onError(error);
      baseOptions.onError?.(error);
    },
  });
};
